import { readonly, shallowRef } from "vue";

/*
 * UX roadmap #63: localStorage state that belongs to one member but should outlive their sign-out, so it is kept per
 * member instead of being purged (USER_SCOPED_PURGE_KEYS): what they have read, what the bell has shown them. It used
 * to be one key per browser, so the next account on a shared device inherited it.
 *
 * The owner is the same opaque id the cache is stamped with (deriveCacheOwnerId), never an email or account id. The
 * Auth store sets it whenever the token changes; signed out, it is null and the unsuffixed key is used.
 */

const owner = shallowRef< string | null >( null );

/** Whose state the owner-scoped keys hold now. Reactive: readers re-run when another member signs in. */
export const storageOwner = readonly( owner );

/** Only the Auth store calls this, from its token watch. */
export function setStorageOwner( value: string | null ): void {
    owner.value = value;
}

/** `base` for a signed-out visitor, `base:<owner>` for a member. */
export function ownerScopedKey( base: string, ownerId: string | null ): string {
    return ownerId ? `${ base }:${ ownerId }` : base;
}

/** Owners whose key has been checked for the unsuffixed value this page load. */
const adopted = new Set< string >();

/**
 * Hands the unsuffixed value to `ownerId` the first time that member has no value of their own on this device, then
 * removes it. It holds what this browser recorded before the keys were per member, which most likely belongs to whoever
 * signs in on it next, and what a signed-out visitor recorded since. Checked once per owner per page load. Never throws.
 */
export function adoptUnscopedValue( base: string, ownerId: string | null ): void {
    if ( !ownerId || adopted.has( `${ base }\n${ ownerId }` ) ) {
        return;
    }
    adopted.add( `${ base }\n${ ownerId }` );

    try {
        const storage: Storage | undefined = globalThis.localStorage;
        const unscoped: string | null | undefined = storage?.getItem( base );
        if ( null == unscoped ) {
            return;
        }

        const key: string = ownerScopedKey( base, ownerId );
        if ( null == storage?.getItem( key ) ) {
            storage?.setItem( key, unscoped );
        }
        storage?.removeItem( base );
    } catch {
        // localStorage unavailable (private mode, quota): the member starts with no state, as before.
    }
}

/** Test seam: a fresh page load, signed out. */
export function resetStorageOwner(): void {
    owner.value = null;
    adopted.clear();
}
